/**
 * Verify a mobile number by WhatsApp code.
 *
 * Used in two places with one flow:
 *
 *   signup  - on the details screen, holding the registrationToken. Success
 *             hands back a mobileVerificationToken for complete-registration.
 *             Optional: "skip" is always offered (decided 7 Oct).
 *   profile - on the account page, holding the session. Success records the
 *             number on the account; the caller refreshes the session.
 *
 * NOT a <form>. At signup this sits inside the profile form, and a nested form
 * is invalid HTML that browsers resolve by letting Enter in the code box submit
 * the OUTER form -- "Create account" -- instead of verifying. So the code box
 * handles Enter itself and every button is type="button".
 *
 * Sending is always an explicit click, never automatic on open: each message
 * costs money, and a customer who only meant to look should not be charged a
 * code.
 */

import React, { FC, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle } from 'react-feather';

import Input from 'shared/Input/Input';
import ButtonPrimary from 'shared/Button/ButtonPrimary';
import {
    authErrorMessage,
    isAccountExistsError,
    isThrottledError,
    MobileVerificationCaller,
    requestMobileVerification,
    throttleRetryAfterSeconds,
    verifyMobile
} from 'service/authApi';

/** Matches the server's RESEND_INTERVAL_SECONDS; the server enforces it, this is the countdown. */
const RESEND_SECONDS = 60;
const CODE_LENGTH = 6;

const onlyDigits = (value: string) => value.replace(/\D/g, '').slice(0, CODE_LENGTH);

export interface WhatsAppVerifyPanelProps {
    /** The 10-digit number to verify. India only, as the server is. */
    mobile: string;
    caller: MobileVerificationCaller;
    context: 'signup' | 'profile';
    onVerified: (proof: { mobileVerificationToken?: string }) => void;
    /** Skip (signup) or close (profile). */
    onDismiss: () => void;
}

const WhatsAppVerifyPanel: FC<WhatsAppVerifyPanelProps> = ({ mobile, caller, context, onVerified, onDismiss }) => {
    const [phase, setPhase] = useState<'ready' | 'code'>('ready');
    const [code, setCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [resendIn, setResendIn] = useState(0);
    const [showSignIn, setShowSignIn] = useState(false);

    useEffect(() => {
        if (resendIn <= 0) return;
        const timer = window.setTimeout(() => setResendIn((seconds) => seconds - 1), 1000);
        return () => window.clearTimeout(timer);
    }, [resendIn]);

    const send = async () => {
        setBusy(true);
        setError('');
        setShowSignIn(false);
        try {
            const data = await requestMobileVerification(mobile, caller);
            if (data?.alreadyVerified) {
                onVerified({});
                return;
            }
            setCode('');
            setPhase('code');
            setResendIn(RESEND_SECONDS);
        } catch (err) {
            // Throttled by the gap between sends: a code went out moments ago
            // and is already on the phone, so the code box is where they want to
            // be. Anything longer is the per-number cap -- stay put and say so.
            const retryAfter = isThrottledError(err) ? throttleRetryAfterSeconds(err) : undefined;
            if (retryAfter && retryAfter <= RESEND_SECONDS) {
                setPhase('code');
                setResendIn(retryAfter);
            }
            // At signup the number may already belong to an account; the way
            // out of that is signing in, not verifying.
            if (isAccountExistsError(err)) setShowSignIn(true);
            setError(authErrorMessage(err, "We couldn't send a code to that number right now."));
        } finally {
            setBusy(false);
        }
    };

    const verify = async () => {
        if (code.length !== CODE_LENGTH || busy) return;
        setBusy(true);
        setError('');
        try {
            const data = await verifyMobile(mobile, code, caller);
            onVerified({ mobileVerificationToken: data?.mobileVerificationToken });
        } catch (err) {
            setError(authErrorMessage(err, 'That code was not correct. Please try again.'));
        } finally {
            setBusy(false);
        }
    };

    // No-break space: the code and the number stay on one line.
    const number = `+91 ${mobile}`;

    return (
        <div
            className="space-y-4 rounded-2xl border border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-700 dark:bg-neutral-800"
            data-testid="whatsapp-verify-panel"
        >
            <div className="flex gap-3">
                <MessageCircle size={20} className="mt-0.5 flex-shrink-0 text-green-600" aria-hidden="true" />
                <div className="min-w-0 text-sm">
                    <p className="font-medium text-neutral-900 dark:text-neutral-100">Verify on WhatsApp</p>
                    <p className="mt-1 text-neutral-600 dark:text-neutral-400">
                        {phase === 'ready' ? (
                            <>
                                We&apos;ll send a 6-digit code to WhatsApp on <span className="font-medium">{number}</span>.
                                {context === 'signup' && ' Optional — it lets you sign in with WhatsApp later.'}
                            </>
                        ) : (
                            <>
                                Enter the code we sent to WhatsApp on <span className="font-medium">{number}</span>. It expires in 10
                                minutes.
                            </>
                        )}
                    </p>
                </div>
            </div>

            {phase === 'ready' && (
                <ButtonPrimary type="button" onClick={send} disabled={busy} loading={busy} className="w-full">
                    {busy ? 'Sending code...' : 'Send code on WhatsApp'}
                </ButtonPrimary>
            )}

            {phase === 'code' && (
                <div className="space-y-3">
                    <Input
                        type="text"
                        aria-label="WhatsApp verification code"
                        autoComplete="one-time-code"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={CODE_LENGTH}
                        autoFocus
                        placeholder="123456"
                        value={code}
                        onChange={(event) => setCode(onlyDigits(event.target.value))}
                        onPaste={(event) => {
                            const pasted = event.clipboardData.getData('text');
                            if (!pasted) return;
                            event.preventDefault();
                            setCode(onlyDigits(pasted));
                        }}
                        onKeyDown={(event) => {
                            // Enter must verify THIS code, never submit the
                            // surrounding signup form. See the header.
                            if (event.key === 'Enter') {
                                event.preventDefault();
                                verify();
                            }
                        }}
                        sizeClass="h-12 px-4"
                        fontClass="text-xl font-medium"
                        className="text-center"
                        style={{ borderColor: error ? '#ea5455' : '', letterSpacing: '0.4em', textIndent: '0.4em' }}
                    />
                    <ButtonPrimary type="button" onClick={verify} disabled={busy || code.length !== CODE_LENGTH} loading={busy} className="w-full">
                        {busy ? 'Verifying...' : 'Verify number'}
                    </ButtonPrimary>
                    <div className="flex justify-end text-sm">
                        <button
                            type="button"
                            className="text-green-600 hover:underline disabled:cursor-not-allowed disabled:text-neutral-400 disabled:no-underline"
                            onClick={send}
                            disabled={busy || resendIn > 0}
                        >
                            {resendIn > 0 ? `Resend code in ${resendIn}s` : 'Resend code'}
                        </button>
                    </div>
                </div>
            )}

            {error && (
                <p role="alert" className="text-sm text-red-600">
                    {error}{' '}
                    {showSignIn && (
                        <Link to="/login" className="font-medium text-green-600 hover:underline">
                            Sign in instead
                        </Link>
                    )}
                </p>
            )}

            <button type="button" className="text-sm text-neutral-500 hover:underline dark:text-neutral-400" onClick={onDismiss} disabled={busy}>
                {context === 'signup' ? 'Skip — continue without verifying' : 'Not now'}
            </button>
        </div>
    );
};

export default WhatsAppVerifyPanel;
